import { useRef } from 'react';
import type { ProbeFormCard } from '../probe-store';

/**
 * Probecover Stage 1 — one card in the /figuren form.
 *
 * 1:1 port of the booth's `.person` fieldset
 * (feat/booth-local:src/booth/public/booth.html + .css + .js).
 * Wording is the booth's wording verbatim (Sebastian approval
 * 2026-10-15) for all card controls: Person, Tier, Person N, Tier N,
 * Entfernen, FOTO, ALTER, Jahre, GESCHLECHT, weiblich, männlich,
 * Keine Angabe. The two reserved slots ("Neue Fotogruppe" + "Cover
 * erstellen") stay placeholders on the parent route.
 *
 * Design tokens: the PWA already carries the booth's four brand
 * tokens as Tailwind-v4 custom-property colors in `src/styles/
 * index.css` (`--color-dream-navy`, `--color-dream-gold`,
 * `--color-dream-light-blue`, `--color-dream-white`). The booth's
 * CSS maps 1:1 to Tailwind utilities over those tokens — no custom
 * CSS added here.
 *
 * Shape (left column 96 px, right column flex):
 *   Head row:  [Person|Tier] segmented toggle · "Person N" label ·
 *              "Entfernen" ghost button
 *   Body (Person branch):
 *     Left:  96 px square dashed-border tile labelled "FOTO" that
 *            opens the file picker on click; shows the thumbnail
 *            via background-image once a file is picked.
 *     Right: ALTER number input + "Jahre" placeholder, then
 *            GESCHLECHT segmented buttons (weiblich · männlich ·
 *            Keine Angabe) in booth order.
 *   Body (Tier branch):
 *     Left:  same 96 px photo tile.
 *     Right: Tierart (Hund · Katze · free-text "Andere (z.B. Pony)")
 *            + ALTER + Jahre placeholder.
 */

export interface ProbeCardProps {
  readonly card: ProbeFormCard;
  readonly index: number;
  readonly canRemove: boolean;
  readonly onUpdate: (patch: Partial<ProbeFormCard>) => void;
  readonly onRemove: () => void;
  readonly onPickPhoto: (file: File | null) => void;
}

const TOGGLE_WRAP =
  'inline-flex rounded-md border border-dream-navy/15 bg-dream-white p-[2px]';
const TOGGLE_BTN =
  'rounded-[6px] px-3.5 py-1.5 text-[13px] font-semibold text-dream-navy/70';
const TOGGLE_BTN_ACTIVE = 'bg-dream-navy text-dream-white';

const FIELD_LABEL = 'text-[12px] font-semibold uppercase tracking-[0.04em] text-dream-navy/70';
const FIELD_INPUT =
  'w-full rounded-md border border-dream-navy/15 bg-dream-white px-3 py-2.5 text-base text-dream-navy outline-none focus:outline-2 focus:outline-dream-gold';

const SEG_WRAP =
  'flex gap-1 rounded-md border border-dream-navy/15 bg-dream-white p-1';
// Sebastian ruling 2026-11-02 (field finding, device "iPhone SE"
// 375x667): narrow horizontal padding on the segmented buttons gives
// "Keine Angabe" enough room to render on one line at 320/375px
// without changing font-size or label text. Desktop (sm+) restores
// the pre-fix 1.5-unit padding, same visual as before.
const SEG_BTN =
  'flex-1 min-w-0 rounded-[6px] px-1 sm:px-1.5 py-2 text-[13px] font-medium text-dream-navy/70 text-center';
const SEG_BTN_ACTIVE = 'bg-dream-navy text-dream-white';

export function ProbeCard({
  card,
  index,
  canRemove,
  onUpdate,
  onRemove,
  onPickPhoto,
}: ProbeCardProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const isTier = card.personType === 'tier';
  const prefix = isTier ? 'Tier' : 'Person';
  const label = `${prefix} ${index + 1}`;

  const openPicker = (): void => fileRef.current?.click();

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    onPickPhoto(file);
  };

  return (
    <fieldset
      data-testid={`probe-card-${index}`}
      data-type={card.personType}
      className="rounded-xl border border-dream-navy/10 bg-dream-light-blue p-3.5 m-0"
    >
      {/* ── Head row: [Person|Tier] · "Person N" · Entfernen ── */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className={TOGGLE_WRAP} role="group" aria-label="Person oder Tier">
          <button
            type="button"
            onClick={() => onUpdate({ personType: 'person', species: '' })}
            aria-pressed={!isTier}
            className={`${TOGGLE_BTN} ${!isTier ? TOGGLE_BTN_ACTIVE : ''}`}
          >
            Person
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ personType: 'tier' })}
            aria-pressed={isTier}
            className={`${TOGGLE_BTN} ${isTier ? TOGGLE_BTN_ACTIVE : ''}`}
          >
            Tier
          </button>
        </div>
        <span className="flex-1 min-w-0 truncate text-[14px] font-semibold text-dream-navy/70">
          {label}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Entfernen"
            className="rounded-md border border-dream-navy/15 bg-transparent px-2.5 py-1.5 text-[13px] font-medium text-dream-navy/70 hover:bg-dream-light-blue hover:text-dream-navy"
          >
            Entfernen
          </button>
        )}
      </div>

      {/* ── Body: 96 px photo tile · meta grid ──
          Sebastian ruling 2026-11-02 (field finding, device "iPhone
          SE" 375x667): the three GESCHLECHT options (weiblich ·
          männlich · Keine Angabe) do not fit in the narrow right
          column — "weiblich" clipped to "we", "männlich" and "Keine
          Angabe" overlap. The fix lays the body out as a 2-row grid
          so GESCHLECHT drops to row 2 and spans the full card width
          on narrow screens, where there is room for the three
          options side by side. Desktop (sm+) keeps the pre-fix
          shape: photo in col 1, meta stack in col 2, GESCHLECHT
          below the meta stack in col 2. */}
      <div className="grid grid-cols-[96px_1fr] gap-x-3.5 gap-y-2.5">
        {/* Photo tile — click opens picker, shows thumbnail on pick */}
        <button
          type="button"
          onClick={openPicker}
          aria-label="Foto"
          className="relative h-24 w-24 overflow-hidden rounded-md border-2 border-dashed border-dream-navy/15 bg-dream-white"
          style={
            card.photoPreviewUrl !== null
              ? {
                  backgroundImage: `url(${card.photoPreviewUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        >
          {card.photoPreviewUrl === null && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] font-semibold uppercase tracking-[0.04em] text-dream-navy/70">
              FOTO
            </span>
          )}
          <input
            ref={fileRef}
            type="file"
            name="photo"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={onFileChange}
            className="hidden"
          />
        </button>

        {/* Row 1 col 2: text metadata that stays next to the photo
            on every width (ALTER for person; TIERART + ALTER for
            tier). */}
        <div className="flex min-w-0 flex-col gap-2.5">
          {!isTier ? (
            /* ALTER (person) */
            <label className="flex min-w-0 flex-col gap-1">
              <span className={FIELD_LABEL}>ALTER</span>
              <input
                type="number"
                name="ageYears"
                min={0}
                max={99}
                step={1}
                placeholder="Jahre"
                value={card.ageYears ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') return onUpdate({ ageYears: null });
                  const n = Number(raw);
                  if (Number.isFinite(n)) onUpdate({ ageYears: n });
                }}
                className={FIELD_INPUT}
              />
            </label>
          ) : (
            <>
              {/* Tierart (tier) — Hund · Katze · free-text "Andere (z.B. Pony)" */}
              <div className="flex min-w-0 flex-col gap-1">
                <span className={FIELD_LABEL}>TIERART</span>
                <div
                  className="grid grid-cols-[1fr_1fr_2fr] gap-1.5 rounded-md border border-dream-navy/15 bg-dream-white p-1"
                  role="group"
                >
                  <button
                    type="button"
                    aria-pressed={card.species === 'Hund'}
                    onClick={() => onUpdate({ species: 'Hund' })}
                    className={`${SEG_BTN} ${card.species === 'Hund' ? SEG_BTN_ACTIVE : ''}`}
                  >
                    Hund
                  </button>
                  <button
                    type="button"
                    aria-pressed={card.species === 'Katze'}
                    onClick={() => onUpdate({ species: 'Katze' })}
                    className={`${SEG_BTN} ${card.species === 'Katze' ? SEG_BTN_ACTIVE : ''}`}
                  >
                    Katze
                  </button>
                  <input
                    type="text"
                    name="speciesText"
                    placeholder="Andere (z.B. Pony)"
                    value={card.species === 'Hund' || card.species === 'Katze' ? '' : card.species}
                    onChange={(e) => onUpdate({ species: e.target.value })}
                    className="border-0 bg-transparent px-2.5 py-2 text-[13px] text-dream-navy outline-none focus:outline-2 focus:outline-dream-gold"
                  />
                </div>
              </div>
              {/* ALTER (tier) */}
              <label className="flex min-w-0 flex-col gap-1">
                <span className={FIELD_LABEL}>ALTER</span>
                <input
                  type="number"
                  name="animalAge"
                  min={0}
                  max={99}
                  step={1}
                  placeholder="Jahre"
                  value={card.ageYears ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') return onUpdate({ ageYears: null });
                    const n = Number(raw);
                    if (Number.isFinite(n)) onUpdate({ ageYears: n });
                  }}
                  className={FIELD_INPUT}
                />
              </label>
            </>
          )}
        </div>

        {/* GESCHLECHT — booth order: weiblich · männlich · Keine
            Angabe. Shared between Person and Tier branches (Sebastian
            ruling 2026-10-17: animal gender is a customer choice,
            not a silent default — same control + labels + validation
            as the Person branch). Grid placement:
              - narrow: col-span-2 → spans both columns of row 2, full
                card width, three options readable side by side.
              - sm+: col-span-1 + col-start-2 → explicit placement in
                col 2 row 2 (auto-row), restoring the pre-2026-11-02
                layout. */}
        <div className="col-span-2 sm:col-span-1 sm:col-start-2 flex min-w-0 flex-col gap-1">
          <span className={FIELD_LABEL}>GESCHLECHT</span>
          <div className={SEG_WRAP} role="group">
            <button
              type="button"
              aria-pressed={card.geschlecht === 'weiblich'}
              onClick={() => onUpdate({ geschlecht: 'weiblich' })}
              className={`${SEG_BTN} ${card.geschlecht === 'weiblich' ? SEG_BTN_ACTIVE : ''}`}
            >
              weiblich
            </button>
            <button
              type="button"
              aria-pressed={card.geschlecht === 'maennlich'}
              onClick={() => onUpdate({ geschlecht: 'maennlich' })}
              className={`${SEG_BTN} ${card.geschlecht === 'maennlich' ? SEG_BTN_ACTIVE : ''}`}
            >
              männlich
            </button>
            <button
              type="button"
              aria-pressed={card.geschlecht === 'keineAngabe'}
              onClick={() => onUpdate({ geschlecht: 'keineAngabe' })}
              className={`${SEG_BTN} ${card.geschlecht === 'keineAngabe' ? SEG_BTN_ACTIVE : ''}`}
            >
              Keine Angabe
            </button>
          </div>
        </div>
      </div>
    </fieldset>
  );
}
